'use client';
import React, {
  createContext,
  useRef,
  useState,
  useEffect,
  useCallback,
} from 'react';
import { AVAILABLE_LANGUAGE_IDS } from '@/constant/Language';
import {
  LOCAL_STORAGE_LANG_KEY,
  LANGUAGE_IN_SEARCH_PARAM,
} from '@/constant/Constants';
import {
  ApiAttribute,
  Endpoint,
  EndpointAttributes,
} from 'src/context/openapi/types';
import { setSentrySiteContext } from '@/lib/sentry';

const DEFAULT_SDK_LANGUAGE = 'node-v3';

const isValidLanguage = (lang: string | null): lang is string => {
  return (
    lang !== null &&
    (AVAILABLE_LANGUAGE_IDS as readonly string[]).includes(lang.toLowerCase())
  );
};
export interface ConversationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  timestamp: Date;
}

export interface ExplorerContextProps {
  api: string;
  setApi: (api: string) => void;
  siteId?: string;
  setSiteId: (siteId: string) => void;
  siteName?: string;
  setSiteName: (siteName: string) => void;
  endpoint?: Endpoint;
  setEndpoint: (endpoint?: Endpoint) => void;
  apiKey: string;
  setApiKey: (apiKey: string) => void;
  resourceList: ApiAttribute[];
  setResourceList: (resourceList: ApiAttribute[]) => void;
  aiResponseOperationIds: string[];
  setAiResponseOperationIds: (endpoints: string[]) => void;
  endpointAttributes?: EndpointAttributes;
  setEndpointAttributes: (endpointAttributes?: EndpointAttributes) => void;
  responsePayload?: JSON;
  setResponsePayload: (responsePayload?: JSON) => void;
  visibleFieldsOverride: Array<string>;
  setVisibleFieldsOverride: (visibleFieldsOverride: Array<string>) => void;
  aiResponse: React.RefObject<string>;
  selectedSDK: string;
  setSelectedSDK: (selectedSDK: string) => void;
  isStreaming: React.RefObject<boolean>;
  userPrompt: string;
  setUserPrompt: (userPrompt: string) => void;
  validateAndClearStaleSites: (
    authorizedSites?: Array<{ site_id: string; site_name: string }>
  ) => void;
}

export const ExplorerContext = createContext<ExplorerContextProps | null>(null);

export const STORAGE_KEY_SITE_ID = 'cb_api_explorer_site_id';
export const STORAGE_KEY_SITE_NAME = 'cb_api_explorer_site_name';

export const validateSite = (
  siteId: string | undefined,
  siteName: string | undefined,
  authorizedSites?: Array<{ site_id: string; site_name: string }>
): boolean => {
  if (
    !siteId ||
    !siteName ||
    !authorizedSites ||
    authorizedSites.length === 0
  ) {
    return false;
  }

  const isValid = authorizedSites.some(
    (site) => site.site_id === siteId && site.site_name === siteName
  );

  return isValid;
};

export const clearSiteDetailFromStorage = () => {
  if (typeof window !== 'undefined') {
    localStorage.removeItem(STORAGE_KEY_SITE_ID);
    localStorage.removeItem(STORAGE_KEY_SITE_NAME);
  }
};

export const ExplorerContextProvider: React.FC<{
  children: React.ReactNode;
}> = ({ children }) => {
  const [api, setApi] = useState<string>('');
  const [endpoint, setEndpoint] = useState<Endpoint>();
  const [selectedSDK, setSelectedSDKInternal] =
    useState<string>(DEFAULT_SDK_LANGUAGE);
  const [apiKey, setApiKey] = useState<string>('');
  const [siteId, setSiteId] = useState<string | undefined>(undefined);
  const [siteName, setSiteName] = useState<string | undefined>(undefined);
  const [resourceList, setResourceList] = useState<ApiAttribute[]>([]);
  const [aiResponseOperationIds, setAiResponseOperationIds] = useState<
    string[]
  >([]);
  const [endpointAttributes, setEndpointAttributes] =
    useState<EndpointAttributes>();
  const [responsePayload, setResponsePayload] = useState<JSON>();
  const [visibleFieldsOverride, setVisibleFieldsOverride] = useState<
    Array<string>
  >([]);
  const aiResponse = useRef('');
  const isStreaming = useRef(false);
  const [userPrompt, setUserPrompt] = useState<string>('');

  useEffect(() => {
    if (typeof window !== 'undefined') {
      if (siteId && siteName) {
        localStorage.setItem(STORAGE_KEY_SITE_ID, siteId);
        localStorage.setItem(STORAGE_KEY_SITE_NAME, siteName);
      }
    }
  }, [siteId, siteName]);

  // Update Sentry site context when site changes
  useEffect(() => {
    if (siteId && siteName) {
      setSentrySiteContext({ siteId, siteName });
    } else {
      setSentrySiteContext(null);
    }
  }, [siteId, siteName]);

  // Initialize selectedSDK from URL param or localStorage
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const params = new URLSearchParams(window.location.search);
    const langFromUrl = params.get(LANGUAGE_IN_SEARCH_PARAM);

    if (isValidLanguage(langFromUrl)) {
      const validated = langFromUrl.toLowerCase();
      setSelectedSDKInternal(validated);
      localStorage.setItem(LOCAL_STORAGE_LANG_KEY, validated);
      return;
    }

    const stored = localStorage.getItem(LOCAL_STORAGE_LANG_KEY);
    if (isValidLanguage(stored)) {
      setSelectedSDKInternal(stored.toLowerCase());
      return;
    }

    localStorage.setItem(LOCAL_STORAGE_LANG_KEY, DEFAULT_SDK_LANGUAGE);
  }, []);

  const setSelectedSDK = useCallback((lang: string) => {
    const validated = isValidLanguage(lang)
      ? lang.toLowerCase()
      : DEFAULT_SDK_LANGUAGE;
    setSelectedSDKInternal(validated);

    if (typeof window !== 'undefined') {
      localStorage.setItem(LOCAL_STORAGE_LANG_KEY, validated);
    }
  }, []);

  const updateKeyForAttributes = (attributes?: EndpointAttributes) => {
    const updateKeys = (items?: ApiAttribute[]) => {
      items?.forEach((param) => {
        param.key = param.name;
        param.child?.forEach((child) => {
          if (child.name == '__array_item__') {
            child.key = `${param.name}[${child.arrayIndex}]`;
          } else {
            child.key =
              param.type === 'ARRAY'
                ? `${param.name}[${child.arrayIndex}].${child.name}`
                : `${param.name}.${child.name}`;
          }
        });
      });
    };
    updateKeys(attributes?.params);
    updateKeys(attributes?.queryParams);
    updateKeys(attributes?.pathParameters);
    setEndpointAttributes(attributes);
  };
  const updateKeyForDefaultArrayAttributes = (
    attributes?: EndpointAttributes
  ) => {
    const updateKeys = (items?: ApiAttribute[]) => {
      items?.forEach((param) => {
        param.key = param.name;
        param.defaultArrayElement?.forEach((child) => {
          if (child.name == '__array_item__') {
            child.key = `${param.name}[${child.arrayIndex}]`;
          } else {
            child.key =
              param.type === 'ARRAY'
                ? `${param.name}[${child.arrayIndex}].${child.name}`
                : `${param.name}.${child.name}`;
          }
        });
      });
    };
    updateKeys(attributes?.params);
    updateKeys(attributes?.queryParams);
    updateKeys(attributes?.pathParameters);
  };
  updateKeyForDefaultArrayAttributes(endpointAttributes);

  const validateAndClearStaleSites = useCallback(
    (authorizedSites?: Array<{ site_id: string; site_name: string }>) => {
      if (!authorizedSites || authorizedSites.length === 0) {
        if (siteId || siteName) {
          setSiteId(undefined);
          setSiteName(undefined);
          clearSiteDetailFromStorage();
        }
        return;
      }

      if (siteId && siteName) {
        if (!validateSite(siteId, siteName, authorizedSites)) {
          setSiteId(undefined);
          setSiteName(undefined);
          clearSiteDetailFromStorage();
          return;
        }
      }
    },
    [siteId, siteName]
  );

  const value = React.useMemo(
    () => ({
      api,
      setApi,
      endpoint,
      setEndpoint,
      apiKey,
      setApiKey,
      siteId,
      setSiteId,
      siteName,
      setSiteName,
      resourceList,
      setResourceList,
      aiResponseOperationIds,
      setAiResponseOperationIds,
      endpointAttributes,
      setEndpointAttributes: updateKeyForAttributes,
      responsePayload,
      setResponsePayload,
      userPrompt,
      setUserPrompt,
      aiResponse,
      visibleFieldsOverride,
      setVisibleFieldsOverride,
      selectedSDK,
      setSelectedSDK,
      isStreaming,
      validateAndClearStaleSites,
    }),
    [
      api,
      endpoint,
      apiKey,
      siteId,
      siteName,
      resourceList,
      aiResponseOperationIds,
      endpointAttributes,
      responsePayload,
      aiResponse,
      visibleFieldsOverride,
      selectedSDK,
      setSelectedSDK,
      isStreaming,
      userPrompt,
      validateAndClearStaleSites,
    ]
  );

  return (
    <ExplorerContext.Provider value={value}>
      {children}
    </ExplorerContext.Provider>
  );
};
